/**
 * Pure helpers for Arena stats display: net odds formatting, ring proportions, favorite pick label.
 * Used by ArenaStatsBanner.
 */

/**
 * Truncate favorite pick name to max length with ellipsis (e.g. "Match Winner (1X2)" is max).
 */
export function truncateFavoriteBetName(
  name: string,
  maxLength: number
): string {
  if (name.length <= maxLength) return name;
  return name.slice(0, maxLength - 1) + '…';
}

/** Ring circumference in viewBox units (circle r=44 in 0 0 100 100). */
export const ARENA_RING_CIRCUMFERENCE = 2 * Math.PI * 44;

export function formatNetOdds(netOdds: number): string {
  if (netOdds > 0) return `+${netOdds.toFixed(1)}`;
  if (netOdds < 0) return netOdds.toFixed(1);
  return '0.0';
}

/**
 * Proportion of circle for wins / draws / losses (three segments, three colors).
 * Order around ring: wins first, then draws, then losses (clockwise from top).
 */
export function getWinsDrawsLossesRingProportions(
  wins: number,
  draws: number,
  losses: number,
  circumference: number = ARENA_RING_CIRCUMFERENCE
): { winsLength: number; drawsLength: number; lossesLength: number; total: number } {
  const total = wins + draws + losses;
  if (total <= 0) return { winsLength: 0, drawsLength: 0, lossesLength: 0, total: 0 };
  return {
    winsLength: (wins / total) * circumference,
    drawsLength: (draws / total) * circumference,
    lossesLength: (losses / total) * circumference,
    total,
  };
}

/**
 * Proportion of circle for won vs lost odds (for ring segment lengths).
 * @param totalOddsWon - Sum of odds on won picks
 * @param totalOddsLost - Sum of odds on lost picks
 * @param circumference - Ring circumference (default ARENA_RING_CIRCUMFERENCE)
 */
export function getOddsRingProportions(
  totalOddsWon: number,
  totalOddsLost: number,
  circumference: number = ARENA_RING_CIRCUMFERENCE
): { wonLength: number; lostLength: number; total: number } {
  const total = totalOddsWon + totalOddsLost;
  if (total <= 0) return { wonLength: 0, lostLength: 0, total: 0 };
  return {
    wonLength: (totalOddsWon / total) * circumference,
    lostLength: (totalOddsLost / total) * circumference,
    total,
  };
}
